/**
 * O cliente HTTP. Um só, em todo o front — ninguém mais cria instância de axios.
 *
 * Carrega quatro responsabilidades:
 *  1. base da API e o token no `Authorization`;
 *  2. o `X-Painel-Id`, enquanto a API ainda aceita o header de transição;
 *  3. tradução de ProblemDetails para `ErroDaApi` com a lista de campos;
 *  4. a trava do canário, que mora em `trava.ts` e é conferida aqui.
 */

import axios, { AxiosError, type AxiosRequestConfig } from "axios"
import { ErroDaApi, ErroDeTrava, traduzirErro } from "./erros"
import { conferirTrava } from "./trava"

// A barra no fim importa. Sem baseURL, o axios resolve "api/pinouts" RELATIVO a
// rota atual: dentro de /projetos/6/pinout a chamada vira /projetos/6/api/pinouts,
// o Vite devolve o index.html com 200 (fallback de SPA) e o front recebe HTML
// achando que e JSON. Com "/" a url sempre sai da raiz.
const BASE = import.meta.env.VITE_API_URL || "/"

/** Id do projeto canário. Vazio = sem trava (é o build de produção). */
const bruto = (import.meta.env.VITE_TRAVA_PROJETO || "").trim()
export const projetoCanario: number | null = bruto === "" ? null : Number(bruto)
export const travaLigada = projetoCanario !== null

// ------------------------------------------------------------------- sessão
// O token mora aqui e não em `localStorage` lido a cada requisição: quem grava
// é o módulo de sessão, que também é quem sabe a hora de expirar.

let tokenAtual: string | null = null
let painelAtual: number | null = null
let aoPerderSessao: (() => void) | null = null

export function definirSessao(token: string | null, painelId: number | null) {
  tokenAtual = token
  painelAtual = painelId
}

/** Chamado quando a API devolve 401 — o módulo de sessão desloga. */
export function aoExpirarSessao(fn: () => void) {
  aoPerderSessao = fn
}

/**
 * Marca uma requisição como "não é de projeto" — catálogos globais, login,
 * acessos. Use sabendo o que está liberando.
 *
 *   api.post("api/login", dados, foraDoProjeto())
 *
 * **Isto não é um passe livre para escrever.** A trava só deixa passar escrita
 * sem projeto quando a rota está na lista curta de "escreve mas não grava"
 * (login, pré-visualização de texto). Qualquer outra escrita marcada assim é
 * recusada em desenvolvimento, porque escrita sem projeto é escrita em dado
 * global — e para dado global não existe canário.
 */
export function foraDoProjeto(config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return { ...config, headers: { ...config.headers, "X-Sem-Projeto": "1" } }
}

/**
 * Declara para a trava em qual projeto esta escrita mexe, quando a própria
 * requisição não diz — o caso de `DELETE api/componentes/{id}`, que só tem o id
 * do recurso na URL.
 *
 * O header é lido pelo interceptor e **removido antes de sair**: a API nunca o
 * vê. É afirmação de quem chama para a trava, não parâmetro de negócio.
 *
 *   await api.delete(`api/componentes/${id}`, noProjeto(projetoId))
 */
export function noProjeto(projetoId: number, config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return { ...config, headers: { ...config.headers, "X-Projeto-Alvo": String(projetoId) } }
}

/**
 * Declara para a trava o nome do registro global que esta escrita atinge.
 *
 * Só serve para o `DELETE`, que não tem corpo de onde ler o nome — na criação e
 * na alteração a trava lê do próprio corpo. Como os outros dois, o header é
 * conversa com a trava e sai antes de ir para a rede.
 *
 *   await api.delete(`api/marcas/${id}`, deTeste(marca.nome, foraDoProjeto()))
 *
 * O nome tem de começar por `ZZTESTE`; o porquê está no `trava.ts`.
 */
export function deTeste(nome: string, config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return { ...config, headers: { ...config.headers, "X-Alvo-De-Teste": nome } }
}

// ------------------------------------------------------------------ cliente

export const api = axios.create({
  baseURL: BASE,
  timeout: 120_000,
  headers: { "Content-Type": "application/json" },

  /**
   * **Array vira o mesmo nome repetido**, e não `nome[]=a&nome[]=b`.
   *
   * O padrão do axios é o segundo, e o ASP.NET Core não liga `nome[]` a um
   * `List<string>`: o parâmetro chega vazio e a rota devolve **tudo**, sem erro
   * nenhum. Foi o que aconteceu com o filtro de status do feedback — a tela
   * pedia "a fazer" e recebia os 73.
   *
   * É um filtro que falha para o lado errado: silencioso, e mostrando mais do
   * que foi pedido.
   */
  paramsSerializer: {
    serialize: (params: Record<string, unknown>) => {
      const p = new URLSearchParams()
      for (const [chave, valor] of Object.entries(params)) {
        if (valor === undefined || valor === null) continue
        if (Array.isArray(valor)) {
          for (const v of valor) if (v !== undefined && v !== null) p.append(chave, String(v))
        } else {
          p.append(chave, String(valor))
        }
      }
      return p.toString()
    },
  },
})

api.interceptors.request.use((config) => {
  conferirTrava(
    {
      method: config.method,
      url: config.url,
      params: config.params,
      data: config.data,
      semProjeto: Boolean(config.headers?.["X-Sem-Projeto"]),
      projetoDeclarado: config.headers?.["X-Projeto-Alvo"]
        ? Number(config.headers["X-Projeto-Alvo"])
        : undefined,
      alvoDeTeste: config.headers?.["X-Alvo-De-Teste"]
        ? String(config.headers["X-Alvo-De-Teste"])
        : undefined,
    },
    projetoCanario,
  )

  if (tokenAtual) config.headers.set("Authorization", `Bearer ${tokenAtual}`)

  // Transição: a API ainda aceita o header enquanto o `PainelContext` não lê
  // do token. Quando ler, esta linha sai.
  if (painelAtual != null) config.headers.set("X-Painel-Id", String(painelAtual))

  // Os três são conversa com a trava, não com a API: saem antes de ir para a rede.
  config.headers.delete("X-Sem-Projeto")
  config.headers.delete("X-Projeto-Alvo")
  config.headers.delete("X-Alvo-De-Teste")

  // FormData manda o proprio Content-Type, com o boundary dentro
  // (`multipart/form-data; boundary=----WebKitFormBoundary...`). O padrao
  // `application/json` da instancia nao sai do caminho sozinho: o axios so
  // decide o tipo quando ninguem o declarou, e aqui esta declarado. O resultado
  // e 415 no servidor, que foi o que aconteceu na primeira planilha em lote --
  // e teria acontecido calado no upload de arquivo do conteudo, que a trava
  // nunca deixou exercer em desenvolvimento.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    config.headers.delete("Content-Type")
  }

  return config
})

api.interceptors.response.use(
  (r) => {
    // Se um dia a base voltar a ficar errada, o sintoma e receber o index.html
    // com 200. Melhor estourar aqui, com o nome do culpado, do que deixar o
    // componente quebrar dez linhas adiante tentando ler `.itens` de uma string.
    const tipo = String(r.headers?.["content-type"] ?? "")
    if (tipo.includes("text/html")) {
      throw new ErroDaApi(
        `A rota ${r.config.url} devolveu HTML, nao JSON. Quase sempre e baseURL errada ` +
          `ou proxy do Vite fora do ar.`,
        0, [], null,
      )
    }
    return r
  },
  (erro: unknown) => {
    if (erro instanceof ErroDeTrava) {
      // Grita no console com o motivo inteiro: em desenvolvimento isso tem que
      // doer, não passar despercebido num toast.
      console.error(erro.message)
      return Promise.reject(erro)
    }

    const ax = erro as AxiosError
    const status = ax.response?.status ?? 0

    if (status === 401 && aoPerderSessao) aoPerderSessao()

    return Promise.reject(traduzirErro(status, ax.response?.data, ax.message))
  },
)

// --------------------------------------------------------------- atalhos

export async function obter<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const r = await api.get<T>(url, { params })
  return r.data
}

export async function enviar<T>(url: string, corpo?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const r = await api.post<T>(url, corpo, config)
  return r.data
}
